// Renderer-side hover for MSSQL. Brings MSSQL to parity with PG / MySQL /
// Oracle / SQLite (each of which already ships a client-side hover that
// surfaces column metadata from its dialect-native schema snapshot).
//
// SSTS (the SQL Tools Service LSP) returns only sparse hover info for table
// identifiers — typically just the object's name and category — so the
// suggest widget that already shows column types is the only place users
// see them today. This module reads the bus-shaped `SchemaSnapshot` (the
// same data the MSSQL resolver consumes) and renders a markdown tooltip
// with the column list + types, matching the behaviour the user expects
// when hovering on `dbo.EnumFixture` in a query.
//
// `getMssqlRoutineHoverInfo` (added in Plan 20 §10 step 3, "hover in-house")
// covers user routines (procs + scalar/table-valued functions) the same
// way — the schema-bus snapshot's `routines` array is populated by
// `mssqlAdapter.ts`'s `projectMssqlSnapshot` from `fetchMssqlRoutines`
// (parameter signatures included). It is DELIBERATELY a separate export
// from `getMssqlHoverInfo`: `getMssqlHoverInfo` (tables) is called
// unconditionally by SqlMonaco's always-on bus-based table-card hover
// provider, which fires for every engine independent of LSP state. The
// in-house mssql provider (`mssqlLanguageServiceProvider.ts`) composes
// ONLY the routine lookup, so the two hover providers never render the
// identical table card twice — see that file's `getHoverInfo` for the
// full rationale.
//
// The identifier-parsing plumbing (dotted-reference reading, bracket/quote
// unwrapping, case-folding) shared by both lookups lives in
// `mssqlHoverIdentifier.ts`.

import type { RoutineMeta, SchemaSnapshot, TableMeta } from '@/core/engines/intellisenseTypes';
import type { LanguageServicePosition } from './languageServiceProvider';
import {
  eqFold,
  getLine,
  readDottedReferenceAt,
  type DottedSegment,
} from './mssqlHoverIdentifier';

type HoverResult = {
  contents: { kind: 'markdown'; value: string };
  range?: { start: LanguageServicePosition; end: LanguageServicePosition };
};

/**
 * Find the table that the dotted reference resolves to. Supports 1-, 2-, and
 * 3-part identifiers across all engines:
 *   - MSSQL / PG / Oracle: 2-part = `<schema>.<table>`; 3-part = `<database>.<schema>.<table>`.
 *   - MySQL / SQLite: 2-part = `<database>.<table>`; no separate schema.
 *
 * The second-to-last part is matched against EITHER `t.schema` OR `t.database`
 * so the same function works for every engine the bus projects into.
 */
function resolveTable(
  segments: readonly DottedSegment[],
  tables: readonly TableMeta[],
): TableMeta | null {
  if (segments.length === 0) return null;
  const parts = segments.map((s) => s.unquoted);
  const tableName = parts[parts.length - 1]!;
  const middlePart = parts.length >= 2 ? parts[parts.length - 2]! : null;
  const outerPart = parts.length >= 3 ? parts[parts.length - 3]! : null;

  for (const t of tables) {
    if (!eqFold(t.name, tableName)) continue;
    if (middlePart) {
      const middleMatches = eqFold(t.schema ?? '', middlePart) || eqFold(t.database ?? '', middlePart);
      if (!middleMatches) continue;
    }
    if (outerPart && !eqFold(t.database ?? '', outerPart)) continue;
    return t;
  }
  return null;
}

function formatTableHover(table: TableMeta): string {
  const qualifiedName = [table.schema, table.name]
    .filter((p) => p && p.length > 0)
    .join('.');
  const kindLabel = table.kind === 'view' ? 'view' : 'table';
  const columnLines = table.columns
    .slice(0, 30)
    .map((c) => {
      const flags = [
        c.isPrimaryKey ? '**PK**' : '',
        c.nullable ? '' : '`NOT NULL`',
      ].filter(Boolean).join(' ');
      return `- \`${c.name}\` \`${c.dataType}\`${flags ? ` ${flags}` : ''}`;
    })
    .join('\n');
  const more = table.columns.length > 30
    ? `\n- …and ${table.columns.length - 30} more`
    : '';
  const columnsBlock = table.columns.length > 0
    ? `\n\n${columnLines}${more}`
    : '\n\n_no columns introspected yet_';
  return `**${kindLabel}** \`${qualifiedName}\`${columnsBlock}`;
}

export function getMssqlHoverInfo(options: {
  text: string;
  position: LanguageServicePosition;
  snapshot?: SchemaSnapshot;
}): HoverResult | null {
  if (!options.snapshot) return null;
  const lineText = getLine(options.text, options.position.line);
  if (!lineText) return null;

  const ref = readDottedReferenceAt(lineText, options.position.character);
  if (!ref) return null;

  const table = resolveTable(ref.segments, options.snapshot.tables);
  if (!table) return null;

  const hovered = ref.segments[ref.hoveredIndex]!;
  return {
    contents: { kind: 'markdown', value: formatTableHover(table) },
    range: {
      start: { line: options.position.line, character: hovered.start },
      end: { line: options.position.line, character: hovered.end },
    },
  };
}

/**
 * Find the routine (procedure/function) that the dotted reference resolves
 * to. Mirrors `resolveTable`'s 1-/2-part matching: last segment is the
 * routine name, the segment before it (if any) is the schema. MSSQL
 * routines don't carry a `database` part in `RoutineMeta` (unlike tables),
 * so a 3-part reference (`db.schema.routine`) still matches on the last
 * two segments only.
 */
function resolveRoutine(
  segments: readonly DottedSegment[],
  routines: readonly RoutineMeta[],
): RoutineMeta | null {
  if (segments.length === 0) return null;
  const parts = segments.map((s) => s.unquoted);
  const routineName = parts[parts.length - 1]!;
  const schemaPart = parts.length >= 2 ? parts[parts.length - 2]! : null;

  for (const r of routines) {
    if (!eqFold(r.name, routineName)) continue;
    if (schemaPart && !eqFold(r.schema ?? '', schemaPart)) continue;
    return r;
  }
  return null;
}

function formatRoutineHover(routine: RoutineMeta): string {
  const qualifiedName = [routine.schema, routine.name]
    .filter((p) => p && p.length > 0)
    .join('.');
  const kindLabel = routine.kind === 'procedure' ? 'procedure' : 'function';
  const paramList = routine.parameters
    .map((p) => {
      const modeFlag = p.mode === 'out' || p.mode === 'inout' ? ' OUT' : '';
      return `\`${p.name}\` \`${p.dataType}\`${modeFlag}`;
    })
    .join(', ');
  const returnsLine = routine.returns ? `\n\nReturns \`${routine.returns}\`` : '';
  return `**${kindLabel}** \`${qualifiedName}\`(${paramList})${returnsLine}`;
}

/**
 * Routine-only counterpart to `getMssqlHoverInfo`. See the file header for
 * why this is a separate export rather than a fallback branch inside
 * `getMssqlHoverInfo` — it exists so the in-house mssql provider can add
 * routine hover without re-rendering the table card the always-on bus hover
 * already shows.
 */
export function getMssqlRoutineHoverInfo(options: {
  text: string;
  position: LanguageServicePosition;
  snapshot?: SchemaSnapshot;
}): HoverResult | null {
  if (!options.snapshot) return null;
  const lineText = getLine(options.text, options.position.line);
  if (!lineText) return null;

  const ref = readDottedReferenceAt(lineText, options.position.character);
  if (!ref) return null;

  const routine = resolveRoutine(ref.segments, options.snapshot.routines);
  if (!routine) return null;

  const hovered = ref.segments[ref.hoveredIndex]!;
  return {
    contents: { kind: 'markdown', value: formatRoutineHover(routine) },
    range: {
      start: { line: options.position.line, character: hovered.start },
      end: { line: options.position.line, character: hovered.end },
    },
  };
}
